/**
 * AiSummaryDialog — one-page AI briefing (PHR Round C).
 *
 * Two sections rendered from the cached summary doc:
 *   - Current picture (always shown)
 *   - What's changed since last visit (only for returning clients)
 *
 * A "Stale — regenerate?" banner appears when the backend detects new
 * timeline entries added since the last generation (via signature diff).
 *
 * Actions:
 *   - Regenerate                 → POST /health-record/{email}/ai-summary
 *   - Copy each section          → clipboard
 *   - Insert into new note       → close + open TreatmentNoteDialog with
 *                                  ``initialSummaryBlock`` pre-filled
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, Loader2, RefreshCw, Copy, FilePlus2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import AIProvenanceBadge from "@/components/AIProvenanceBadge";
import AiSourcesToggle from "@/components/AiSourcesToggle";

export default function AiSummaryDialog({ email, onClose, onInsertIntoNote }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState(null);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/health-record/${encodeURIComponent(email)}/ai-summary`);
      setSummary(r.data?.summary || null);
      setStale(!!r.data?.stale);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => { refresh(); }, [refresh]);

  async function generate() {
    setGenerating(true);
    setError(null);
    try {
      const r = await api.post(`/health-record/${encodeURIComponent(email)}/ai-summary`);
      setSummary(r.data?.summary || null);
      setStale(false);
      toast.success("AI summary generated");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Generation failed", { description: msg });
    } finally {
      setGenerating(false);
    }
  }

  async function copyToClipboard(text, label) {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${label} copied`);
    } catch {
      toast.error("Clipboard blocked", { description: "Select and copy manually." });
    }
  }

  const hasSummary = !!summary?.current_picture;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="ai-summary-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            AI patient summary
            <AIProvenanceBadge
              confidence={summary?.confidence}
              reviewed={!!summary?.reviewed_by}
              reviewedBy={summary?.reviewed_by?.name || summary?.reviewed_by?.email}
              reviewedAt={summary?.reviewed_at}
              source="health record + timeline"
              testId="ai-summary-provenance-badge"
            />
            {stale && hasSummary && (
              <span
                className="text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800"
                data-testid="ai-summary-stale-badge"
              >
                Stale — regenerate?
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            One-page briefing generated from this client&apos;s health record. Uses the same LLM that
            powers SOAP notes. Every statement is grounded in stored records — never invent history.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-1 -mr-1 space-y-4">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !hasSummary ? (
            <div className="p-8 text-center border border-dashed border-zinc-200 rounded-md space-y-3">
              <Sparkles className="h-8 w-8 mx-auto text-indigo-500" />
              <div className="text-sm text-zinc-700">
                No AI summary yet for this client. Generate one to get a one-page briefing before
                the next appointment.
              </div>
              <Button
                onClick={generate}
                disabled={generating}
                className="bg-indigo-700 hover:bg-indigo-800 text-white"
                data-testid="ai-summary-generate"
              >
                {generating ? (
                  <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating…</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-1.5" /> Generate AI summary</>
                )}
              </Button>
              {error && (
                <div className="text-xs text-red-600 flex items-center gap-1 justify-center">
                  <AlertCircle className="h-3.5 w-3.5" /> {error}
                </div>
              )}
            </div>
          ) : (
            <>
              {stale && (
                <div className="p-2.5 rounded-md border border-amber-200 bg-amber-50 text-xs text-amber-900 flex items-center justify-between gap-3">
                  <span>New records have been added since this summary was generated.</span>
                  <Button
                    size="sm" variant="outline"
                    onClick={generate}
                    disabled={generating}
                    className="border-amber-400 bg-white text-amber-900"
                    data-testid="ai-summary-regen-stale"
                  >
                    {generating
                      ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Regenerating…</>
                      : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Regenerate</>}
                  </Button>
                </div>
              )}

              <SummarySection
                title="Current picture"
                text={summary.current_picture}
                testid="ai-summary-current-picture"
                onCopy={() => copyToClipboard(summary.current_picture, "Current picture")}
                sources={summary.sources_used}
                sourcesTestId="ai-summary-sources-current"
              />

              {summary.whats_changed ? (
                <SummarySection
                  title="What's changed since last visit"
                  text={summary.whats_changed}
                  testid="ai-summary-whats-changed"
                  onCopy={() => copyToClipboard(summary.whats_changed, "What's changed")}
                  sources={summary.sources_used}
                  sourcesTestId="ai-summary-sources-changed"
                />
              ) : (
                <div className="p-3 border border-dashed border-zinc-200 rounded-md text-xs text-zinc-500 italic" data-testid="ai-summary-first-visit">
                  First recorded visit for this client — no prior appointment to compare against.
                </div>
              )}

              <div className="text-2xs text-zinc-500 flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-100">
                <span>Generated {fmtWhen(summary.generated_at)}</span>
                {summary.generated_by_name && <span>· by {summary.generated_by_name}</span>}
                {summary.model && <span>· {summary.model}</span>}
                {summary.source_counts && (
                  <span className="text-zinc-400">
                    · sources: {Object.entries(summary.source_counts)
                      .filter(([, v]) => v > 0)
                      .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
                      .join(", ") || "none"}
                  </span>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            {hasSummary && (
              <>
                <Button
                  variant="outline" size="sm"
                  onClick={generate}
                  disabled={generating}
                  data-testid="ai-summary-regen"
                >
                  {generating
                    ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Regenerating…</>
                    : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Regenerate</>}
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => copyToClipboard(formatCombined(summary), "Summary")}
                  data-testid="ai-summary-copy-all"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy all
                </Button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {hasSummary && (
              <Button
                onClick={() => onInsertIntoNote?.(summary.current_picture)}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="ai-summary-insert-note"
              >
                <FilePlus2 className="h-4 w-4 mr-1.5" /> Insert into new note
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SummarySection({ title, text, testid, onCopy, sources, sourcesTestId }) {
  return (
    <div className={cn("border border-zinc-200 rounded-md p-3 bg-white")} data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs uppercase tracking-wider font-semibold text-zinc-700">{title}</div>
        <button
          onClick={onCopy}
          className="p-1 rounded hover:bg-zinc-100 text-zinc-500"
          title="Copy section"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="text-sm text-zinc-900 whitespace-pre-wrap leading-relaxed">
        {text}
      </div>
      <AiSourcesToggle sources={sources} testId={sourcesTestId} />
    </div>
  );
}


function formatCombined(summary) {
  if (!summary) return "";
  const parts = [`# Current picture\n\n${summary.current_picture || ""}`];
  if (summary.whats_changed) {
    parts.push(`\n\n# What's changed since last visit\n\n${summary.whats_changed}`);
  }
  return parts.join("");
}


function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-AU", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
